'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { buildRoute, Position, RATING_REVIEWS_MENU_ID, ThirdPartSelectors } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { SideMenuModal, useUIContext } from '@headless-workspace/core-ui';
import { useProductDetailContext, useUserInfo } from '@headless-workspace/domain/common/client';
import { getConnectedUserInfo } from '@headless-workspace/domain/common/server';
import {
    GlowButton,
    GlowCheckbox,
    GlowClickable,
    GlowProgressBar,
    GlowTextBody,
    PropsWithLocale,
    Target,
} from '@headless-workspace/glow-ds';
import { Component, ProductType } from '@headless-workspace/typings';
import { UrlHelpers } from '@headless-workspace/utils';
import {
    ProductReview,
    RatingFilterData,
    ReviewSortOption,
} from '../../../../../src/server/lib/productDetail/ratingsAndReviews';
import { ProductInformationLoader } from '../information';
import { RatingsAndReviewsFilter } from './RatingsAndReviewsFilter';
import { RatingsAndReviewsModal } from './RatingsAndReviewsModal';
import { ReviewCard } from './ReviewCard';
import { ReviewScoreFilter } from './ReviewScoreFilter';
import { useFilteredReviews } from './useFilteredReviews';
import { useRatingsAndReviewsForm } from './useRatingsAndReviewsForm';

type RatingsAndReviewsContentProps = PropsWithLocale & {
    reviewCount: number;
    baseReviews: ProductReview[];
    ratingFilter: RatingFilterData[];
    rating: number;
    maxRating: number;
    sortingOptions: ReviewSortOption[];
    productId: string;
};

const PRODUCT_DETAIL_REVIEWS = 'product-detail-reviews';
const PRODUCT_DETAIL_RATINGS = 'product-detail-ratings';
const PRODUCT_DETAIL_RATINGS_AND_REVIEWS_CONTENT = 'product-detail-ratings-and-reviews-content';
const RATING_ALL = 'rating-all';
const PROGRESS_BAR_MIN_VALUE = 0;
const PROGRESS_BAR_MAX_VALUE = 100;
const PDP_REVIEWS_REDIRECT_KEY = 'isBvPopin';
const PDP_REVIEWS_REDIRECT_VALUE = 'true';
const PDP_REVIEWS_LAST_VISITED_PID_KEY = 'lastVisitedPID';

export const RatingsAndReviewsContent: Component<RatingsAndReviewsContentProps> = ({
    reviewCount,
    baseReviews,
    locale,
    rating,
    ratingFilter,
    sortingOptions,
    maxRating,
    productId,
}) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviews');

    const { productDetails } = useProductDetailContext();

    const searchParams = useSearchParams();
    const isRedirectFromLogin = useRef(
        searchParams.get(PDP_REVIEWS_REDIRECT_KEY) === PDP_REVIEWS_REDIRECT_VALUE,
    ).current;

    const { result: userInfoResult, isLoading: isLoadingUserInfo } = useUserInfo();

    const [shouldFetchForm, setShouldFetchForm] = useState<boolean>(false);
    const [openFormModal, setOpenFormModal] = useState<boolean>(false);

    const userInfo = useMemo(() => getConnectedUserInfo(userInfoResult), [userInfoResult]);
    const { isLoading: isLoadingRatingsAndReviewsForm, result: resultRatingsAndReviewsForm } = useRatingsAndReviewsForm(
        productId,
        shouldFetchForm,
    );

    const sku = productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : undefined;

    const additionalRedirectToLoginParams = sku
        ? {
              [PDP_REVIEWS_REDIRECT_KEY]: PDP_REVIEWS_REDIRECT_VALUE,
              [PDP_REVIEWS_LAST_VISITED_PID_KEY]: sku,
          }
        : undefined;

    const {
        menuModal: { addIfAbsentAndOpen },
    } = useUIContext();

    const {
        onRatingChange,
        onOptionUpdate,
        onResetSelectedRatings,
        onSeeMoreReviewsClick,
        selectedRatings,
        displayedReviews,
        reviews,
        isMutating,
    } = useFilteredReviews({
        baseReviews,
        reviewCount,
        productId,
    });

    const filteredReviewsCount = reviews.length;

    const displayRatingAndReviewsModal = () => {
        setShouldFetchForm(true);
        setOpenFormModal(true);
    };

    useEffect(() => {
        if (!isRedirectFromLogin) return;

        UrlHelpers.removeSearchParamsFromUrl([PDP_REVIEWS_REDIRECT_KEY]);

        if (userInfo) {
            displayRatingAndReviewsModal();
        }
    }, [isRedirectFromLogin, userInfo]);

    useEffect(() => {
        if (!openFormModal) return;

        const ratingsAndReviewsForm = unwrapResultSuccess(resultRatingsAndReviewsForm);

        if (ratingsAndReviewsForm && userInfo) {
            setOpenFormModal(false);
            const modalInfo = {
                modalId: RATING_REVIEWS_MENU_ID,
                modal: SideMenuModal({
                    side: Position.Right,
                    map: {
                        default: (
                            <RatingsAndReviewsModal
                                ratingAndReviewsForm={ratingsAndReviewsForm}
                                locale={locale}
                                productId={productId}
                                userInfo={userInfo}
                            />
                        ),
                    },
                }),
            };
            addIfAbsentAndOpen(modalInfo.modalId, modalInfo.modal);
        }
    }, [addIfAbsentAndOpen, openFormModal, resultRatingsAndReviewsForm, userInfo, locale, productId]);

    const giveMyOpinionButtonAction = userInfo
        ? { onClick: displayRatingAndReviewsModal }
        : { href: buildRoute(locale, 'connection', additionalRedirectToLoginParams) };

    const firstCheckboxSelected = selectedRatings.length === 0 && reviewCount > 0;
    const hasNoReviews = reviewCount === 0;
    const progressBarValue = reviewCount > 0 ? PROGRESS_BAR_MAX_VALUE : PROGRESS_BAR_MIN_VALUE;

    const shouldShowLoader = isRedirectFromLogin && (isLoadingUserInfo || isLoadingRatingsAndReviewsForm);

    return (
        <>
            {shouldShowLoader && <ProductInformationLoader />}
            <RatingsAndReviewsFilter
                reviewCount={reviewCount}
                rating={rating}
                maxRating={maxRating}
                sortingOptions={sortingOptions}
                onValueUpdate={onOptionUpdate}
            />
            <section
                id={PRODUCT_DETAIL_RATINGS_AND_REVIEWS_CONTENT}
                className={'flex flex-col tablet:flex-row tablet:flex-wrap gap-2.5 tablet:relative'}
            >
                <div id={PRODUCT_DETAIL_RATINGS} className={'tablet:flex-1 flex flex-col gap-2.5'}>
                    <div className={'flex flex-col gap-1'}>
                        <div className={'flex gap-1 items-center justify-center'}>
                            <div className={'flex gap-0.25 items-center justify-center'}>
                                <GlowCheckbox
                                    id={RATING_ALL}
                                    onChange={onResetSelectedRatings}
                                    checked={firstCheckboxSelected}
                                    disabled={hasNoReviews}
                                />
                                <GlowTextBody text={t('label.all', { reviewCount })} className={'w-maxContent'} />
                            </div>
                            <GlowProgressBar value={progressBarValue} />
                        </div>

                        <ReviewScoreFilter
                            ratingFilter={ratingFilter}
                            onCheckboxChange={onRatingChange}
                            selectedRatings={selectedRatings}
                            disableCheckboxes={hasNoReviews}
                        />
                    </div>
                    {reviewCount > 0 && (
                        <div className={'flex flex-col gap-0.5'}>
                            <GlowButton
                                label={t('action.giveMyOpinion')}
                                {...giveMyOpinionButtonAction}
                                isSpinnerActive={isLoadingRatingsAndReviewsForm && shouldFetchForm}
                            />
                            <GlowClickable
                                content={{
                                    labelElement: <GlowTextBody text={t('action.reviewConditions')} />,
                                }}
                                href={buildRoute(locale, 'reviewConditions')}
                                className={'text-underline'}
                                target={Target.Blank}
                            />
                            {/* To reserve the place for 3rd partyskeepers FHEAD-1764*/}
                            <div className={'h-[5rem] w-full'} id={ThirdPartSelectors.ProductCustomerVideoCta}></div>
                        </div>
                    )}
                </div>
                <div id={PRODUCT_DETAIL_REVIEWS} className={'tablet:flex-1 max-w-[38.25rem]'}>
                    {reviewCount > 0 ? (
                        <>
                            <ul className={'flex flex-col gap-1.5'}>
                                {reviews.map((review, index) => (
                                    <ReviewCard
                                        key={review.id}
                                        // FIXME: adapt this in FHEAD-235
                                        className={clsx(index >= displayedReviews && 'hidden')}
                                        {...review}
                                        locale={locale}
                                        TagName={'li'}
                                    />
                                ))}
                            </ul>
                            <div className={'px-1 py-1.5 flex flex-col items-center gap-1'}>
                                <div className={'flex flex-col items-center gap-0.5 w-reviewCounter'}>
                                    <GlowProgressBar value={displayedReviews} max={filteredReviewsCount} />
                                    <GlowTextBody
                                        text={t('label.reviewCounter', {
                                            current:
                                                displayedReviews > filteredReviewsCount
                                                    ? filteredReviewsCount
                                                    : displayedReviews,
                                            total: filteredReviewsCount,
                                        })}
                                        className={'w-fit'}
                                    />
                                </div>
                                {filteredReviewsCount > displayedReviews && (
                                    <GlowButton
                                        dataTestId={'see-more-reviews-button'}
                                        label={t('action.seeMoreReviews')}
                                        onClick={onSeeMoreReviewsClick}
                                        className={'self-stretch desktopS:self-auto'}
                                        variant={'secondary'}
                                        isSpinnerActive={isMutating}
                                    />
                                )}
                            </div>
                        </>
                    ) : (
                        <div
                            className={
                                'flex flex-col justify-center items-center gap-0.5 px-1.5 py-2 border-solid border-medium border-border-primary rounded-0.5'
                            }
                        >
                            <GlowTextBody text={t('label.noReviews')} />
                            <GlowButton
                                label={t('action.giveMyOpinion')}
                                {...giveMyOpinionButtonAction}
                                className={'w-full'}
                                isSpinnerActive={isLoadingRatingsAndReviewsForm && shouldFetchForm}
                            />
                        </div>
                    )}
                </div>
                {hasNoReviews && (
                    <GlowClickable
                        content={{
                            labelElement: <GlowTextBody text={t('action.reviewConditions')} />,
                        }}
                        href={buildRoute(locale, 'reviewConditions')}
                        className={'text-underline w-full'}
                        target={Target.Blank}
                    />
                )}
            </section>
        </>
    );
};
